import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppBar, Toolbar, IconButton, Typography, Box, Button } from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import OutdoorGrillRounded from '@mui/icons-material/OutdoorGrillRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import type { Screen } from '../../App';
import { setLanguage, type SupportedLanguage } from '../../i18n';
import { FlameMark } from '../../assets/FlameMark';

interface AppHeaderProps {
  screen: Screen;
  tableNumber?: number | null;
  pendingCount: number;
  onBack: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  screen,
  tableNumber,
  pendingCount,
  onBack,
  onOpenSettings,
  onLogout,
}) => {
  const { t, i18n } = useTranslation();

  const showBack = screen === 'order' || screen === 'bill';
  const showFireIcon =
    screen === 'tables' || screen === 'kitchen' || screen === 'admin' || screen === 'stats' || screen === 'tenants';
  const showKitchenPill = (screen === 'tables' || screen === 'admin') && pendingCount > 0;
  const showSubtitle = screen === 'kitchen' && pendingCount > 0;

  const title =
    screen === 'tables'
      ? t('appBar.titleTables')
      : screen === 'order'
      ? t('appBar.titleOrder', { num: tableNumber })
      : screen === 'bill'
      ? t('appBar.titleBill', { num: tableNumber })
      : screen === 'kitchen'
      ? t('appBar.titleKitchen')
      : screen === 'stats'
      ? t('appBar.titleStats')
      : screen === 'tenants'
      ? t('tenants.title')
      : t('appBar.titleAdmin');

  const toggleLanguage = (): void => {
    const next: SupportedLanguage = i18n.language === 'de' ? 'en' : 'de';
    setLanguage(next);
  };

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ gap: 0.5, px: 1 }}>
        {showBack && (
          <IconButton onClick={onBack} sx={{ color: '#fff' }}>
            <ArrowBackRounded />
          </IconButton>
        )}
        {showFireIcon && (
          <FlameMark sx={{ color: '#fff', fontSize: 28, mx: 1 }} cutoutColor="#B71C1C" />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0, px: 1 }}>
          <Typography variant="h6" noWrap sx={{ color: '#fff' }}>
            {title}
          </Typography>
          {showSubtitle && (
            <Typography variant="body2" noWrap sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
              {t('appBar.pendingOrders', { count: pendingCount })}
            </Typography>
          )}
        </Box>
        {showKitchenPill && (
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 20,
              px: 1.5,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mr: 1,
            }}
          >
            <OutdoorGrillRounded sx={{ color: '#fff', fontSize: 16 }} />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem' }}>{pendingCount}</Typography>
          </Box>
        )}
        <Button onClick={toggleLanguage} sx={{ color: '#fff', minWidth: 0, px: 1 }}>
          {i18n.language === 'de' ? 'EN' : 'DE'}
        </Button>
        <IconButton onClick={onOpenSettings} sx={{ color: '#fff' }}>
          <SettingsRounded />
        </IconButton>
        <IconButton onClick={onLogout} sx={{ color: '#fff' }}>
          <LogoutRounded />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
